import router from "./../router";
import { version as appVersion } from "../../package.json";
import globals from "../globals";
import feature from "../feature";
import { TextEncoder } from "text-encoding-shim";
import { store } from "../surveyjs/store";
import Live from "./Live";

const base64url = require("base64url");
if (process.env.NODE_ENV == "test") {
  console.log("process.env.NODE_ENV == \"test\"");
  process.auth = {};
}
const SERVER_URL = process.auth.serverUrl;
const CLIENT_URL = process.auth.redirectUrl;
const KEYCLOAK_URL = process.auth.keyCloakUrl;
const KEYCLOAK_SURVEYCODE_SECRET = process.auth.keyCloakSurveyCodeSecret;

export default {
  // app wide variables
  tokenRenewalPending: false,
  tokenRenewalCallbacks: [],
  appReloadPending: false,
  dynamicDomain: null,
  // app functions
  /**
   * Server Url is undefined at app start so we return it dynamically
   * @returns The auth server url
   */
  getServerUrl() {
    return SERVER_URL;
  },
  isJWTExpired(token) {
    let payload = this.getTokenData(token);
    let expireDate = payload.exp * 1000;
    let now = Date.now();
    return now > expireDate;
  },
  /**
   * Allow using alternative Redirect Url if the feature is activated
   * @param {String} url
   * @returns if not allowed
   */
  allowAlternativeRedirectUrl(url) {
    if (!feature.isEnabled("MultipleDomains") || !MULTIPLE_REDIRECTS) return;

    const urlArray = url.split("/");
    MULTIPLE_REDIRECTS.forEach((redirect) => {
      if (
        urlArray.length >= 5 &&
        urlArray.slice(0, 4).join("/") + "/" === redirect
      ) {
        this.dynamicDomain = redirect;
      }
    });
    if (!this.dynamicDomain) {
      this.dynamicDomain = CLIENT_URL;
    }
  },
  /**
   * @returns ClientURL
   */
  getClientURL() {
    // #1313
    if (feature.isEnabled("MultipleDomains") && !this.dynamicDomain)
      this.allowAlternativeRedirectUrl(window.location.href);
    if (this.dynamicDomain) {
      return this.dynamicDomain;
    } else {
      return CLIENT_URL;
    }
  },
  isSurveyCodeUser() {
    let token = this.getAccessToken();
    let payload = this.getTokenData(token);
    return payload && !!payload.survey_code;
  },
  updateTokenDataDependencies() {
    globals.isSurveyCodeUser = this.isSurveyCodeUser();
  },
  isUserAuthenticated() {
    let access_token = this.getAccessToken();
    return access_token ? !this.isJWTExpired(access_token) : false;
  },
  getTokenData(token) {
    if (!token) return;
    let data = JSON.parse(window.atob(token.split(".")[1]));
    return data;
  },
  getAccessToken() {
    return localStorage.getItem("access_token") || null;
  },
  getIdToken() {
    return localStorage.getItem("id_token") || null;
  },
  checkRouteValid(hash) {
    if (hash.length > 2) {
      var route = hash.slice(1);
      for (var r in router.options.routes) {
        var path = router.options.routes[r].path;
        if (path == route) return true;
      }
    }
    return false;
  },
  randomString(length) {
    let charset =
      "0123456789ABCDEFGHIJKLMNOPQRSTUVXYZabcdefghijklmnopqrstuvwxyz-._~";
    let result = "";

    while (length > 0) {
      let bytes = new Uint8Array(16);
      let crypto = window.crypto || window.msCrypto;
      let random = crypto.getRandomValues(bytes);

      random.forEach(function (c) {
        if (length == 0) {
          return;
        }
        if (c < charset.length) {
          result += charset[c];
          length--;
        }
      });
    }
    return result;
  },
  hexString(buffer) {
    const byteArray = new Uint8Array(buffer);

    const hexCodes = [...byteArray].map((value) => {
      const hexCode = value.toString(16);
      const paddedHexCode = hexCode.padStart(2, "0");
      return paddedHexCode;
    });

    return hexCodes.join("");
  },
  async digestMessage(message) {
    const encoder = new TextEncoder();
    const data = encoder.encode(message);
    let crypto = window.crypto || window.msCrypto;
    let result = await crypto.subtle.digest("SHA-256", data);
    if (result.result) result = result.result; // IE11 returns an object containing result property
    return result;
  },
  async digestMessageToHexString(message) {
    return this.hexString(await this.digestMessage(message));
  },
  directToLogin(surveyCodeLogin, surveyCode) {
    this.tokenRenewalPending = true;
    const nonce = this.randomString(4096);
    const stateNonce = this.randomString(4096);
    const codeVerifier = this.randomString(128);
    this.digestMessageToHexString(nonce).then((nonceDigest) => {
      this.digestMessageToHexString(stateNonce).then((stateDigest) => {
        this.digestMessage(codeVerifier).then((codeVerifierDigest) => {
          let urlData = this.getTokensFromUrl();
          let stateDigestFromUrl = urlData && urlData.state_digest;
          let state;
          let stateDigestForRedirect = stateDigest;
          if (surveyCodeLogin) {
            state = "/surveyview";
            sessionStorage.setItem(stateDigest, state);
          } else if (stateDigestFromUrl) {
            state = sessionStorage.getItem(stateDigestFromUrl);
            stateDigestForRedirect = stateDigestFromUrl;
          } else {
            if (location.pathname.indexOf("survey.html") > -1)
              state = location.pathname + location.hash;
            else state = router.currentRoute.fullPath;
            sessionStorage.setItem(stateDigest, state);
          }
          sessionStorage.setItem("nonce_digest", nonceDigest);
          localStorage.setItem("code_verifier", codeVerifier);

          let codeVerifierDigestBase64 = base64url.encode(codeVerifierDigest);
          let postLoginRedirectUrl = this.getClientURL() + "callback";

          let fullAuthUrl = `${SERVER_URL}connect/authorize?client_id=loopShopperFrontend&redirect_uri=${encodeURIComponent(
            postLoginRedirectUrl
          )}&response_type=code&scope=openid%20profile%20offline_access%20loopShopperBackend%20loopOpBackend&state=${stateDigestForRedirect}&nonce=${nonceDigest}&code_challenge_method=S256&code_challenge=${codeVerifierDigestBase64}`;

          if (surveyCodeLogin) {
            fullAuthUrl += `&survey_code_login=true`;
            if (surveyCode) fullAuthUrl += `&survey_code=${surveyCode}`;
          }

          window.location.href = fullAuthUrl;
        });
      });
    });
  },
  redirectAfterLoginCallback() {
    let tokens = this.getTokensFromUrl();
    let codeVerifier = localStorage.getItem("code_verifier");
    if (!tokens || !tokens.code || !codeVerifier) {
      console.error("Missing code in URL");
      this.removeTokensFromStorage();
      this.directToLogin();
      return;
    }

    this.tokenRenewalPending = true;
    let formData = new FormData();
    formData.append("client_id", "loopShopperFrontend");
    formData.append("code_verifier", codeVerifier);
    formData.append("code", tokens.code);
    formData.append("grant_type", "authorization_code");
    formData.append("redirect_uri", `${this.getClientURL()}callback`);
    $.ajax({
      method: "POST",
      url: `${SERVER_URL}connect/token`,
      data: formData,
      contentType: false,
      processData: false,
      global: false,
      success: (data) => {
        if (data && data.access_token && data.refresh_token && data.id_token) {
          this.storeTokensInStorage(
            data.access_token,
            data.id_token,
            data.refresh_token
          );
          this.updateTokenDataDependencies();
          this.configureAjaxSetup();
          this.setClientTimeZone();

          this.tokenRenewalCallbacks.forEach((callback) => {
            callback();
          });
          this.tokenRenewalCallbacks = [];

          let nonceFromToken = this.getTokenData(data.id_token).nonce;
          let nonceFromStorage = sessionStorage.getItem("nonce_digest");
          sessionStorage.removeItem("nonce_digest");
          let stateFromStorage;
          if (tokens.state_digest) {
            stateFromStorage = sessionStorage.getItem(tokens.state_digest);
            sessionStorage.removeItem(tokens.state_digest);
          }
          if (nonceFromStorage !== nonceFromToken || !stateFromStorage) {
            this.directToLogin();
          } else {
            if (stateFromStorage.indexOf("survey.html") > -1)
              location = window.origin + stateFromStorage;
            else router.push(stateFromStorage);
          }
        }
      },
      error: () => {
        console.error("Error on POST in redirectAfterLoginCallback()");
        this.removeTokensFromStorage();
        this.directToLogin();
      },
      complete: () => {
        this.tokenRenewalPending = false;
      },
    });
  },
  setClientTimeZone() {
    let tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!tz) return;
    let query = `api/SecurityUsers/Localization?TimeZoneName=${encodeURIComponent(
      tz
    )}`;

    $.ajax({
      method: "PUT",
      url: query,
      dataType: "json",
      contentType: "application/json; charset=utf-8",
      error: () => {
        console.error("Error on PUT in setClientTimeZone()");
        globals.showErrorSnackbar("Timezone update failed");
      },
    });
  },
  getDefaultAjaxSetupConfig() {
    return {
      headers: {
        "Cache-Control": "no-cache",
      },
      beforeSend: (jqXHR, options) => {
        if (!this.isUserAuthenticated() && !options.isAuthRequest) {
          let repeatAjax = () => {
            $.ajax(
              $.extend(options, {
                beforeSend: $.noop,
                headers: {
                  Authorization: "Bearer " + this.getAccessToken(),
                },
              })
            );
          };
          this.renewToken(repeatAjax);
          return false;
        }
      },
    };
  },
  configureAjaxSetup() {
    let access_token = this.getAccessToken();
    if (!access_token) {
      console.error("No access token available");
      this.directToLogin();
      return;
    }
    let config = this.getDefaultAjaxSetupConfig();
    config.headers.Authorization = `Bearer ${access_token}`;
    $.ajaxSetup(config);
  },
  initializeGlobalAjaxEvents() {
    if (!localStorage.getItem("refresh_token") && !this.tokenRenewalPending) {
      this.removeTokensFromStorage();
      this.directToLogin();
      return;
    }

    let access_token = this.getAccessToken();
    this.updateTokenDataDependencies();
    let config = this.getDefaultAjaxSetupConfig();
    if (access_token) config.headers.Authorization = `Bearer ${access_token}`;
    $.ajaxSetup(config);

    window.addEventListener("storage", (e) => {
      if (e.key === "refresh_token") {
        this.configureAjaxSetup();
      }
    });

    $(document).ajaxComplete((event, jqXHR, options, data) => {
      let apiVersion = jqXHR.getResponseHeader("shopper-version");
      let isDevSystem = feature.currentTarget.indexOf("dev") > -1;
      // if (
      //   !isDevSystem &&
      //   !this.appReloadPending &&
      //   apiVersion &&
      //   apiVersion !== appVersion
      // ) {
      //   this.appReloadPending = true;
      //   // Force hard reload of the page if a new version of the app is available
      //   console.error(`Frontend version mismatch - reloading page \n Api-Version: ${apiVersion}  App-Version: ${appVersion}`);
      //   Live.Live.clearSiteDataAll()
      //   .then((res) => {
      //     location.reload(true);
      //   })
      //   .catch(() => {
      //     console.error("Live error");
      //   });
      // }

      if (jqXHR.status === 401) {
        this.renewToken(() => {
          $.ajax(
            $.extend(options, {
              beforeSend: $.noop,
              headers: {
                Authorization: "Bearer " + this.getAccessToken(),
              },
            })
          );
        });
      }
    });
  },
  /**
   * Renew the acces token if we have a refresh_token or code_verifier
   * @param {Function} doneCallback
   * @returns
   */
  renewToken(doneCallback) {
    if(this.isKeycloakUser()) {
      this.renewKeycloakToken(doneCallback);
      return;
    }

    if (doneCallback) this.tokenRenewalCallbacks.push(doneCallback);
    if (this.tokenRenewalPending) return;

    this.tokenRenewalPending = true;
    let refreshToken = localStorage.getItem("refresh_token");
    let codeVerifier = localStorage.getItem("code_verifier");
    if (!refreshToken || !codeVerifier) {
      this.removeTokensFromStorage();
      this.directToLogin();
      return;
    }
    let formData = new FormData();
    formData.append("client_id", "loopShopperFrontend");
    formData.append("code_verifier", codeVerifier);
    formData.append("refresh_token", refreshToken);
    formData.append("grant_type", "refresh_token");
    $.ajax({
      method: "POST",
      url: `${SERVER_URL}connect/token`,
      data: formData,
      contentType: false,
      processData: false,
      global: false,
      isAuthRequest: true,
      success: (data) => {
        if (data && data.access_token && data.refresh_token && data.id_token) {
          this.storeTokensInStorage(
            data.access_token,
            data.id_token,
            data.refresh_token
          );
          this.updateTokenDataDependencies();
          this.configureAjaxSetup();

          this.tokenRenewalCallbacks.forEach((callback) => {
            callback();
          });
          this.tokenRenewalCallbacks = [];
        }
      },
      error: (data) => {
        console.error("Error on POST in renewToken()");
        this.removeTokensFromStorage();
        this.directToLogin();
      },
      complete: () => {
        this.tokenRenewalPending = false;
      },
    });
  },
  getPostLoginUrl() {
    let hashString = window.location.hash;
    let idx = hashString.indexOf("id_token");
    if (idx < 0) return "";

    let postLoginUrl = hashString.slice(2, idx);
    return postLoginUrl;
  },
  storeTokensInStorage(access_token, id_token, refresh_token) {
    localStorage.setItem("access_token", access_token);
    localStorage.setItem("id_token", id_token);
    localStorage.setItem("refresh_token", refresh_token);
    globals.$emit("accessTokenChanged");
  },
  removeTokensFromStorage() {
    localStorage.removeItem("access_token");
    localStorage.removeItem("id_token");
    localStorage.removeItem("refresh_token");
    globals.$emit("accessTokenChanged");
  },
  /**
   * @typedef {Object} TokenObject
   * @property {String} code
   * @property {String} state_digest
   */
  /**
   * Creates an object out of the url we got from the auth server
   *
   * @returns {?TokenObject} contains the code and the state_digest
   */
  getTokensFromUrl() {
    let params = {};
    window.location.hash.replace(
      /[?&]+([^=&]+)=([^&]*)/gi,
      function (m, key, value) {
        params[key] = value;
      }
    );

    let result = {};
    if (params.code) result.code = params.code;
    if (params.state) result.state_digest = params.state;

    if (Object.keys(result).length < 1) return null;
    return result;
  },
  logout() {
    let logoutUrl = `${SERVER_URL}connect/endsession`;
    let postLogoutRedirectUrl = this.getClientURL() + "logout_callback";

    const stateNonce = this.randomString(4096);
    this.digestMessageToHexString(stateNonce).then((stateDigest) => {
      let state = router.currentRoute.fullPath;
      if (this.isSurveyCodeUser()) state = `/surveycode`;
      sessionStorage.setItem(stateDigest, state);

      let id_token = this.getIdToken();
      let fullLogoutUrl = `${logoutUrl}?id_token_hint=${id_token}&post_logout_redirect_uri=${encodeURIComponent(
        postLogoutRedirectUrl
      )}&state=${stateDigest}`;

      this.removeTokensFromStorage();
      window.location.href = fullLogoutUrl;
    });
  },
  redirectAfterLogoutCallback() {
    let urlData = this.getTokensFromUrl();
    if (urlData && urlData.state_digest) {
      let state = sessionStorage.getItem(urlData.state_digest);
      sessionStorage.removeItem(urlData.state_digest);
      if (state) {
        router.push(state);
        return;
      }
    }

    this.redirectAfterLoginCallback();
  },
  async accessFromApp(accessCode, languagecode) {
    var surveyInstanceId;
    var isSurveyCode = false;
    let locationLanguage = 'en';

    // get keycloak refreshToken from DB
    try {
      const res = await new Promise((resolve, reject) => {
        $.ajax({
          method: "GET",
          url: `shopper-api/AppAccess/${accessCode}`,
          // headers: {
          //   Authorization: "Bearer " + this.getAccessToken(),
          // },
          dataType: "json",
          contentType: "application/json; charset=utf-8",
          success: (data) => {resolve(data)},
          error: (err) => {reject(err)},
        });
      }); 
      localStorage.setItem("refresh_token", res.refresh_token);
      surveyInstanceId = res.sid;
      isSurveyCode = res.isSurveyCode;
      console.log(res)
    } catch (err) {
      console.error("Error on GET 'shopper-api/AppAccess/{accessCode}' in accessFromApp()", err);
    }
    console.log(isSurveyCode)
    if(isSurveyCode === "True") {
      sessionStorage.setItem("is_surveycode", true);
      sessionStorage.setItem("surveyinstanceid", surveyInstanceId);
    } else {
      sessionStorage.removeItem("is_surveycode");
      sessionStorage.removeItem("surveyinstanceid");
    }

    await this.renewKeycloakToken();

    // get surveyInstance location language
    try {
      const res = await new Promise((resolve, reject) => {
        $.ajax({
          method: "GET",
          url: `api/SurveyInstances/location-language/${surveyInstanceId}`,
          headers: {
            Authorization: "Bearer " + this.getAccessToken(),
          },
          dataType: "json",
          contentType: "application/json; charset=utf-8",
          success: (data) => {resolve(data)},
          error: (err) => {reject(err)},
        });
      }); 
      if(res.Language) locationLanguage = res.Language
    } catch (err) {
      console.error("Error on GET 'api/location-language/{surveyInstanceId}' in accessFromApp()", err);
    }

    window.location.href = `${window.origin}/survey.html#/view/${surveyInstanceId}?lang=${locationLanguage}&frontendLanguage=${languagecode}`;
  },
  async renewKeycloakToken(doneCallback) {
    var accessTokenURL = `${KEYCLOAK_URL}/protocol/openid-connect/token`;

    if (doneCallback) this.tokenRenewalCallbacks.push(doneCallback);
    if (this.tokenRenewalPending) return;

    this.tokenRenewalPending = true;
    let refreshToken = localStorage.getItem("refresh_token");
    if (!refreshToken) {
      this.directToSessionExpired();
      this.removeTokensFromStorage();
      return;
    }

    let data = {
      client_id: "loop-app-client",
      refresh_token: refreshToken,
      grant_type: "refresh_token"
    };

    // for surveycode
    if(sessionStorage.getItem("is_surveycode")) {
      data = {
        client_id: "survey-code-client",
        refresh_token: refreshToken,
        grant_type: "refresh_token",
        client_secret: KEYCLOAK_SURVEYCODE_SECRET,
        external_check_id: sessionStorage.getItem("surveyinstanceid"),
      };
    }


    try {
      const res = await new Promise((resolve, reject) => {
        $.ajax({
          method: "POST",
          url: `${accessTokenURL}`,
          headers: null,
          data: $.param(data),
          contentType: "application/x-www-form-urlencoded; charset=UTF-8",
          processData: false,
          global: false,
          isAuthRequest: true,
          success: (data) => {resolve(data)},
          error: (err) => {reject(err)},
        });
      });
      if (res && res.access_token && res.refresh_token) {
        localStorage.setItem("access_token", res.access_token);
        localStorage.setItem("refresh_token", res.refresh_token);

        this.updateTokenDataDependencies();
        this.configureAjaxSetup();

        this.tokenRenewalCallbacks.forEach((callback) => {
          callback();
        });
        this.tokenRenewalCallbacks = [];

        if (res.expires_in) {
          setTimeout(() => {
            this.renewKeycloakToken();
          }, res.expires_in * 1000 * 0.9);
        }
      }
    } catch (err) {
      this.directToSessionExpired();
      console.error("Error on POST in renewKeycloakToken()", err);
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
    } finally {
      this.tokenRenewalPending = false;
    }
  },
  directToSessionExpired() {
    window.location.replace(`${window.origin}/static/session-expired.html`);
  },
  isKeycloakUser() {
    let access_token = this.getAccessToken();
    if(access_token && this.getTokenData(access_token).iss === KEYCLOAK_URL) {
      return true;
    };
    return false;
  }
};



// WEBPACK FOOTER //
// ./src/services/app.service.js